import { Redirect, useLocalSearchParams } from "expo-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ActivityIndicator, Platform, ScrollView, View } from "react-native";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";
import { SafeAreaView } from "react-native-safe-area-context";

import { Card, CardContent } from "@/components/ui/card";
import { Text } from "@/components/ui/text";
import { MedicalRequestForm } from "@/features/medicalRequest";
import { ActionChildSelector, useChildren } from "@/features/selectedChild";
import { useGetMedicalRequestQuery } from "@/query/apiSlice";

export default function MedicalRequestScreen() {
    const { childId: childIdParam, requestId } = useLocalSearchParams<{
        childId?: string;
        requestId?: string;
    }>();
    const { children, isLoading: isLoadingChildren } = useChildren();
    const [manualSelectedChildId, setManualSelectedChildId] = useState<
        number | null
    >(null);
    const { t } = useTranslation();

    const parsedRequestId =
        requestId && typeof requestId === "string" ? Number(requestId) : null;
    const {
        data: existingRequest,
        isLoading: isLoadingRequest,
        isError: isRequestError,
    } = useGetMedicalRequestQuery(parsedRequestId as number, {
        skip: parsedRequestId == null || Number.isNaN(parsedRequestId),
    });

    const activeExistingRequest = existingRequest?.is_valid
        ? existingRequest
        : undefined;

    const selectedChildId = useMemo(() => {
        if (activeExistingRequest) {
            return activeExistingRequest.child_id;
        }

        if (manualSelectedChildId !== null) {
            return manualSelectedChildId;
        }

        if (childIdParam) {
            return Number(childIdParam);
        }

        if (children.length === 1) {
            return children[0].id;
        }

        return null;
    }, [activeExistingRequest, manualSelectedChildId, childIdParam, children]);

    const selectedChild = useMemo(
        () => children.find((child) => child.id === selectedChildId),
        [children, selectedChildId],
    );

    if (isLoadingChildren || (parsedRequestId != null && isLoadingRequest)) {
        return (
            <View className="flex-1 items-center justify-center bg-background">
                <ActivityIndicator />
            </View>
        );
    }

    if (children.length === 0 || !CONFIG.featureMedicalRequestsEnabled) {
        return <Redirect href="/" />;
    }

    if (parsedRequestId != null && (isRequestError || !activeExistingRequest)) {
        return <Redirect href="/" />;
    }

    const FormScroll =
        Platform.OS === "web" ? ScrollView : KeyboardAwareScrollView;

    return (
        <SafeAreaView className="flex-1 bg-background" edges={["bottom"]}>
            <FormScroll
                className="flex-1"
                contentContainerClassName="px-4 web:w-full web:mx-auto web:max-w-lg"
                keyboardShouldPersistTaps="handled"
                keyboardDismissMode="interactive"
                bottomOffset={24}
            >
                <Card>
                    <CardContent className="gap-4">
                        <ActionChildSelector
                            childList={children}
                            selectedChildId={selectedChildId}
                            onSelectChild={setManualSelectedChildId}
                            disabled={Boolean(activeExistingRequest)}
                        />

                        {selectedChild ? (
                            <MedicalRequestForm
                                child={selectedChild}
                                existingRequest={activeExistingRequest}
                            />
                        ) : (
                            <Text variant="muted">
                                {t("medical.selectStudent")}
                            </Text>
                        )}
                    </CardContent>
                </Card>
            </FormScroll>
        </SafeAreaView>
    );
}
